import { setTextFontScaledToBox } from "@core/Utils/HelperFunction";
import { Constants } from "../../Utils/Constants";
import { Language, Messages } from "../Language";

export class IntroPrompt extends Phaser.GameObjects.Container {
    protected background: Phaser.GameObjects.Image;
    protected continueText: Phaser.GameObjects.Text;
    protected descriptionText: Phaser.GameObjects.Text;
    protected helpHand: Phaser.GameObjects.Sprite;
    protected helpHandAnimationFirstClick: Phaser.Tweens.Tween;
    protected helpHandAnimationSecondClick: Phaser.Tweens.Tween;

    //iOS intro stuff <- the same on all games thus it sits in core
    private iosWebBar: Phaser.GameObjects.Image;
    private iosContextMenu: Phaser.GameObjects.Image;
    private iosBlurredGame: Phaser.GameObjects.Image;


    constructor(public scene: Phaser.Scene, protected onClickCallback: Function) {
        super(scene);

        this.background = new Phaser.GameObjects.Image(scene, 0, 0, Constants.LOADING_BACKGROUND);
        this.background.setOrigin(0);
        this.background.setInteractive();
        this.background.on("pointerdown", (p: Phaser.Input.Pointer, x: number, y: number, e: Phaser.Types.Input.EventData) => {
            e.stopPropagation();
        }, this);
        this.background.on("pointerup", (p: Phaser.Input.Pointer, x: number, y: number, e: Phaser.Types.Input.EventData) => {
            this.onClickCallback();
            e.stopPropagation();
        }, this);
        this.add(this.background);

        if (this.scene.game.device.os.iOS)
            this.drawIOSIntro();
        else
            this.drawIntro();
    }

    override destroy(fromScene?: boolean): void {
        if (this.helpHandAnimationFirstClick)
            this.helpHandAnimationFirstClick.stop();
        if (this.helpHandAnimationSecondClick)
            this.helpHandAnimationSecondClick.stop();
        if (this.background)
            this.background.removeInteractive();
        super.destroy(fromScene);
    }

    resize(width: number, height: number, portraitMode: boolean = false): void {
        this.background.setDisplaySize(width, height);

        if (portraitMode) {
            this.background.setPosition(0, 0);
        }
        else {
            this.background.setPosition(0, 0);
        }

        if (this.scene.game.device.os.iOS)
            this.refreshIOSIntro(width, height, portraitMode);
        else
            this.refreshIntro(width, height, portraitMode);
    }

    drawIOSIntro(): void {
        this.iosBlurredGame = new Phaser.GameObjects.Image(this.scene, 0, 0, Constants.IOS_INTRO_GAME_IMAGE);
        this.iosBlurredGame.setOrigin(0.5, 0);
        this.iosBlurredGame.setAlpha(1, 1, 0, 0);
        this.add(this.iosBlurredGame);

        this.iosWebBar = new Phaser.GameObjects.Image(this.scene, 0, 0, Constants.SPRITESHEET_IOS_TUTORIAL, "iosTab.png");
        this.iosWebBar.setOrigin(0.5, 0);
        this.add(this.iosWebBar);

        this.iosContextMenu = new Phaser.GameObjects.Image(this.scene, 0, 0, Constants.SPRITESHEET_IOS_TUTORIAL, "iosMenu.png");
        this.iosContextMenu.setOrigin(0.5, 0);
        this.iosContextMenu.visible = false;
        this.add(this.iosContextMenu);

        this.helpHand = new Phaser.GameObjects.Sprite(this.scene, 0, 0, Constants.ANIMATIONS.HELPHAND, "tap_0.png");
        this.helpHand.setOrigin(1, 1);
        this.add(this.helpHand);

        this.helpHand.anims.create({
            key: "pointerClick",
            frames: this.helpHand.anims.generateFrameNames(Constants.ANIMATIONS.HELPHAND, {
                prefix: 'tap_',
                end: 7,
                suffix: '.png'
            }),
            frameRate: 30,
            yoyo: true,
            repeat: 0
        });
        this.helpHand.addToUpdateList();

        this.descriptionText = new Phaser.GameObjects.Text(this.scene, 0, 0, Language.Get(Messages.HOW_TO_ENTER_FULLSCREEN), Constants.PANEL.BET_FONT);
        this.descriptionText.setOrigin(0.5, 0.5);
        this.add(this.descriptionText);

        this.continueText = new Phaser.GameObjects.Text(this.scene, 0, 0, Language.Get(Messages.CLICK_TO_CONTINUE).toUpperCase(), Constants.PANEL.BET_FONT);
        this.continueText.setOrigin(0.5, 0.5);
        this.continueText.setAlign("center");
        this.continueText.setShadow(0, 0, "black", 10, false, true);
        this.add(this.continueText);
    }

    refreshIOSIntro(width: number, height: number, portraitMode: boolean = false): void {
        this.iosWebBar.displayWidth = Math.min(width * 0.95, height * 2);
        this.iosWebBar.displayHeight = this.iosWebBar.displayWidth * (this.iosWebBar.height / this.iosWebBar.width);
        this.iosWebBar.setPosition(width / 2, height * 0.1);

        this.iosBlurredGame.displayWidth = this.iosWebBar.displayWidth;
        this.iosBlurredGame.displayHeight = this.iosBlurredGame.displayWidth * (this.iosBlurredGame.height / this.iosBlurredGame.width);
        this.iosBlurredGame.setPosition(width / 2, this.iosWebBar.y + this.iosWebBar.displayHeight - 5);

        this.iosContextMenu.displayWidth = this.iosWebBar.displayWidth * 0.3;
        this.iosContextMenu.displayHeight = this.iosContextMenu.displayWidth * (this.iosContextMenu.height / this.iosContextMenu.width);
        this.iosContextMenu.setPosition(width / 2 - this.iosWebBar.displayWidth / 3.8, this.iosWebBar.y + this.iosWebBar.displayHeight * 0.85);

        this.helpHand.displayWidth = this.iosWebBar.displayHeight * 1.1;
        this.helpHand.displayHeight = this.helpHand.displayWidth * (this.helpHand.height / this.helpHand.width);
        this.helpHand.visible = true;
        this.helpHand.setPosition(0, 0);

        if (this.helpHandAnimationFirstClick) {
            this.helpHandAnimationFirstClick.stop();
            this.helpHandAnimationFirstClick = null;
        }

        this.helpHandAnimationFirstClick = this.scene.tweens.add({
            targets: this.helpHand,
            x: { from: 0, to: this.iosContextMenu.x + this.helpHand.displayWidth / 2 },
            y: { from: 0, to: this.iosContextMenu.y + this.helpHand.displayHeight / 2 },
            ease: 'Linear',
            duration: 650,
            onStart: () => {
                this.helpHandAnimationSecondClick.restart();
            },
            onComplete: () => {
                this.helpHand.play("pointerClick");
                this.helpHand.once('animationcomplete', () => {
                    this.iosContextMenu.visible = true;
                    if (this.helpHandAnimationSecondClick)
                        this.helpHandAnimationSecondClick.play();
                }, this);
            },
            repeat: 0,
            paused: true
        });

        if (this.helpHandAnimationSecondClick) {
            this.helpHandAnimationSecondClick.stop();
            this.helpHandAnimationSecondClick = null;
        }
        this.helpHandAnimationSecondClick = this.scene.tweens.add({
            targets: this.helpHand,
            x: { from: this.iosContextMenu.x + this.helpHand.displayWidth / 2, to: this.iosContextMenu.x - this.iosContextMenu.displayWidth / 4 + this.helpHand.displayWidth / 2 },
            y: { from: this.iosContextMenu.y + this.helpHand.displayHeight / 2, to: this.iosContextMenu.y + this.iosContextMenu.displayHeight * 0.78 + this.helpHand.displayHeight / 2 },
            ease: 'Linear',
            duration: 650,
            onStart: () => {
                this.helpHandAnimationFirstClick.restart();
            },
            onComplete: () => {
                this.helpHand.play("pointerClick");
                this.helpHand.once('animationcomplete', () => {
                    this.iosContextMenu.visible = false;
                    if (this.helpHandAnimationFirstClick)
                        this.helpHandAnimationFirstClick.play();
                }, this);
            },
            repeat: 0,
            paused: false
        });

        this.helpHandAnimationFirstClick.play();

        this.descriptionText.setPosition(width / 2, height * 0.05);
        setTextFontScaledToBox(this.descriptionText, width / 2, height * 0.1);

        this.continueText.setPosition(width - width / 3, height / 2);
        setTextFontScaledToBox(this.continueText, width / 1.5, height / 2);
        this.continueText.setWordWrapWidth(width / 2);
        this.continueText.runWordWrap(this.continueText.text);
    }

    drawIntro(): void { }

    refreshIntro(width: number, height: number, portraitMode: boolean = false): void { }
}